import React from "react";
import Layout from "@/components/Layout";
import { useAnalyticsOptOut } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const DISCLAIMER =
  "This privacy policy describes how Regulens handles information provided by users of this platform. Regulens is not a law firm and this policy does not constitute legal advice.";

const ADMIN_EMAIL = "admin@getregulens.com";

export default function PrivacyPolicy() {
  const { setOptOut, isOptedOut, gaEnabled } = useAnalyticsOptOut();
  const [, force] = React.useReducer((x) => x + 1, 0);
  const optedOut = isOptedOut();
  return (
    <Layout>
      <section className="max-w-3xl mx-auto px-6 py-12" data-testid="privacy-policy-page">
        <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)] mb-3">Legal</div>
        <h1 className="font-heading font-extrabold tracking-tight text-3xl sm:text-4xl text-[var(--rg-text-strong)] mb-4">
          Privacy Policy
        </h1>

        <div
          className="mb-8 rounded-md border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)] p-4 text-sm text-[var(--rg-amber)]"
          data-testid="privacy-disclaimer"
        >
          {DISCLAIMER}
        </div>

        <article className="prose-invert space-y-6 text-[var(--rg-text)] leading-relaxed">
          <section>
            <h2 className="font-heading text-xl text-[var(--rg-text-strong)] mb-2">What data Regulens collects</h2>
            <ul className="list-disc pl-6 space-y-1 text-sm">
              <li>Email addresses and passwords at signup.</li>
              <li>Shopify store URLs entered by merchants.</li>
              <li>Annual revenue tier selections.</li>
              <li>Jurisdiction selections (which states).</li>
              <li>Pasted policy text (Privacy Policy, Terms of Service, Refund Policy text submitted for analysis).</li>
              <li>New York revenue and transaction figures entered for dual-threshold calculation.</li>
              <li>Compliance readiness scores and audit history.</li>
              <li>Usage data and session information.</li>
              <li>IP addresses for security purposes.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl text-[var(--rg-text-strong)] mb-2">How Regulens uses that data</h2>
            <ul className="list-disc pl-6 space-y-1 text-sm">
              <li>To generate informational readiness reports.</li>
              <li>To send compliance digest emails to Pro users.</li>
              <li>To improve the platform.</li>
              <li>For no other purpose.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl text-[var(--rg-text-strong)] mb-2">What Regulens does not do</h2>
            <p className="text-sm">
              Regulens does not sell user data. We do not share data with third parties except as necessary to operate the service:
              Anthropic (Claude API for AI processing), Lemon Squeezy (billing), Resend (email delivery), Google Analytics 4 (anonymized, aggregated page-view analytics — see below), and the hosting provider.
            </p>
          </section>

          {/* 2026-08-18 — Analytics disclosure + user-controlled opt-out.
              Added to close the brand-consistency gap flagged when we
              introduced Google Analytics 4 for aggregate page-view metrics. */}
          <section data-testid="privacy-analytics-section">
            <h2 className="font-heading text-xl text-[var(--rg-text-strong)] mb-2">Analytics &amp; opt-out</h2>
            <p className="text-sm">
              Regulens uses Google Analytics 4 to understand aggregate page-view metrics — how many merchants read the Methodology page, which sample-report sections are most viewed, etc. GA4 is configured with:
            </p>
            <ul className="list-disc pl-6 space-y-1 text-sm mt-2">
              <li><strong>IP anonymization</strong> — Google truncates the last octet before storing.</li>
              <li><strong>No cross-device / Google Signals</strong> — we do not link visits across your devices.</li>
              <li><strong>No ad personalization</strong> — no data is shared into Google&apos;s ad-targeting ecosystem.</li>
              <li><strong>SPA page_view events only</strong> — no scroll depth, no click heatmaps, no session recording.</li>
            </ul>
            <p className="text-sm mt-3">
              If you prefer not to be counted at all, click the button below. Your preference is stored locally in this browser (via a small flag in <code className="text-xs bg-[var(--rg-surface-2)] px-1 rounded">localStorage</code>) and blocks all future analytics events. To re-enable, click again.
            </p>
            <div className="mt-3 flex items-center gap-3" data-testid="privacy-analytics-optout-row">
              {gaEnabled ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const next = !optedOut;
                    setOptOut(next);
                    toast.success(next ? "Analytics disabled for this browser." : "Analytics re-enabled for this browser.");
                    force();
                  }}
                  className="border-[var(--rg-border-hi)] text-[var(--rg-text)] hover:bg-[var(--rg-surface-2)] font-semibold text-xs"
                  data-testid="privacy-analytics-optout-toggle"
                >
                  {optedOut ? "Re-enable analytics" : "Opt out of analytics"}
                </Button>
              ) : (
                <span className="text-xs text-[var(--rg-muted)] italic">Analytics is not currently configured on this environment.</span>
              )}
              {optedOut && (
                <span className="text-xs text-[var(--rg-sage)] font-medium" data-testid="privacy-analytics-optout-status">
                  Currently opted out on this browser.
                </span>
              )}
            </div>
          </section>

          <section>
            <h2 className="font-heading text-xl text-[var(--rg-text-strong)] mb-2">Data retention</h2>
            <p className="text-sm">Data is retained while your account is active and deleted within 30 days of account deletion.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl text-[var(--rg-text-strong)] mb-2">Your rights</h2>
            <ul className="list-disc pl-6 space-y-1 text-sm">
              <li>Right to access your data.</li>
              <li>Right to delete your data (delete account).</li>
              <li>Right to export your data.</li>
            </ul>
            <p className="text-sm mt-2">
              Contact{" "}
              <a href={`mailto:${ADMIN_EMAIL}`} className="text-[var(--rg-sage)] hover:underline" data-testid="privacy-contact-email">
                {ADMIN_EMAIL}
              </a>{" "}
              for these requests.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl text-[var(--rg-text-strong)] mb-2">Third-party processors</h2>
            <p className="text-sm">Anthropic, Lemon Squeezy, Resend, Google Analytics 4 (analytics only — see opt-out above), and our hosting provider.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl text-[var(--rg-text-strong)] mb-2">GDPR acknowledgment</h2>
            <p className="text-sm">
              Regulens complies with applicable data protection laws in jurisdictions where it operates.
            </p>
          </section>

          <p className="text-xs text-[var(--rg-muted)] italic mt-8">This policy was last updated June 2026.</p>
        </article>
      </section>
    </Layout>
  );
}
